<template>
  <article class="crepes-card afro-section-card">
    <FlagLabel :label="category.badge_label || category.name" :icon="category.icon_key || 'star'" />

    <div class="crepe-products" :class="`count-${products.length}`">
      <section
        v-for="(product, productIndex) in products"
        :key="product.id_token"
        class="crepe-product"
        :class="[
          `product-${productIndex + 1}`,
          { 'sold-out': product.availability_status === 'sold_out' }
        ]"
      >
        <header>
          <div>
            <h3>{{ product.name }}</h3>
            <p v-if="product.description">{{ product.description }}</p>
          </div>
          <span v-if="product.availability_status === 'sold_out'" class="product-soldout">Agotada</span>
          <strong v-else>{{ product.price_label || `Desde ${formatPrice(product.price)}` }}</strong>
        </header>

        <p v-if="product.customization_rule" class="pricing-note">
          <span>{{ includedLabel(product.customization_rule) }}</span>
          <span aria-hidden="true">•</span>
          <span>+{{ formatPrice(product.customization_rule.extra_price) }} cada ingrediente extra</span>
        </p>

        <div class="ingredient-groups">
          <section
            v-for="group in product.customization_groups || []"
            :key="group.id_token"
            class="ingredient-group"
            :class="{ wide: (group.options || []).length > 7 }"
          >
            <h4>{{ group.name }}</h4>
            <ul :class="{ columns: (group.options || []).length > 7 }">
              <li
                v-for="option in group.options || []"
                :key="option.id_token"
                :class="{ unavailable: option.availability_status === 'sold_out' }"
              >
                <span>{{ option.name }}</span>
                <small v-if="option.availability_status === 'sold_out'">Agotado</small>
              </li>
            </ul>
          </section>
        </div>
      </section>
    </div>
  </article>
</template>

<script>
import FlagLabel from '@/components/menu/FlagLabel.vue'

export default {
  name: 'CrepesMenuSection',
  components: { FlagLabel },
  props: {
    category: { type: Object, required: true },
    formatPrice: { type: Function, required: true }
  },
  computed: {
    products() {
      return this.category.products || []
    }
  },
  methods: {
    includedLabel(rule) {
      const count = Number(rule.included_count || 0)
      return count === 1 ? '1 ingrediente incluido' : `${count} ingredientes incluidos`
    }
  }
}
</script>

<style scoped>
.crepes-card {
  min-width: 0;
  height: auto;
  padding: clamp(16px, 1.05vh, 21px) clamp(17px, 1.55vw, 24px);
  display: flex;
  flex-direction: column;
}

:deep(.flag-label) {
  width: min(240px, 35%);
  min-height: clamp(40px, 2.35vh, 46px);
  padding: clamp(7px, .5vh, 10px) 28px clamp(7px, .5vh, 10px) 16px;
  font-size: clamp(18px, 1.12vh, 22px);
}

:deep(.flag-label)::after {
  right: -19px;
  border-top-width: clamp(20px, 1.18vh, 23px);
  border-bottom-width: clamp(20px, 1.18vh, 23px);
  border-left-width: 19px;
}

.crepe-products {
  min-height: 0;
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 2.05fr) minmax(210px, .95fr);
  gap: clamp(14px, 1.45vw, 20px);
  align-items: start;
  margin-top: clamp(12px, .8vh, 16px);
}

.crepe-products.count-1 { grid-template-columns: 1fr; }

.crepe-product {
  min-width: 0;
  padding: clamp(12px, .7vh, 14px) clamp(13px, 1.15vw, 18px);
  border: 1px dashed rgba(38, 51, 31, .36);
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(255, 249, 235, .4), rgba(239, 215, 173, .2));
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, .2);
}

.crepe-product > header {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 10px;
}

.crepe-product h3 {
  margin: 0;
  color: var(--afro-olive);
  font-family: var(--afro-text-serif);
  font-size: clamp(22px, 1.32vh, 26px);
  line-height: 1;
}

.crepe-product header p {
  display: none;
  margin: 5px 0 0;
  font-size: clamp(12px, .72vh, 14px);
  font-weight: 700;
  line-height: 1.25;
}

.crepe-product header strong,
.product-soldout {
  flex: 0 0 auto;
  border-radius: 999px;
  padding: 5px 9px;
  background: var(--afro-olive);
  color: #f6dfb9;
  font-size: clamp(12px, .7vh, 14px);
  line-height: 1;
  white-space: nowrap;
}

.product-soldout { background: #b95f57; text-transform: uppercase; }
.crepe-product.sold-out .ingredient-groups { opacity: .48; }

.pricing-note {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 7px;
  margin: clamp(6px, .4vh, 8px) 0 clamp(8px, .5vh, 10px);
  padding: 6px 9px;
  border-radius: 10px;
  background: rgba(200, 153, 60, .12);
  color: #755624;
  font-size: clamp(12px, .72vh, 14px);
  font-weight: 900;
  line-height: 1.15;
}

.ingredient-groups {
  display: grid;
  grid-template-columns: minmax(76px, .7fr) minmax(102px, .95fr) minmax(0, 1.9fr);
  gap: 9px clamp(9px, .85vw, 13px);
  align-items: start;
}

.crepe-products.count-1 .ingredient-groups { grid-template-columns: repeat(3, minmax(0, 1fr)); }

.ingredient-group { min-width: 0; }
.ingredient-group h4 {
  margin: 0 0 5px;
  padding-bottom: 3px;
  border-bottom: 1px solid rgba(38, 51, 31, .18);
  color: var(--afro-olive);
  font-size: clamp(12px, .72vh, 14px);
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: .05em;
}

.ingredient-group ul {
  display: grid;
  gap: 1px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ingredient-group ul.columns { grid-template-columns: 1fr; }

.ingredient-group li {
  display: grid;
  grid-template-columns: 8px minmax(0, 1fr) auto;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
  font-size: clamp(14px, .76vh, 15px);
  line-height: 1.12;
}

.ingredient-group li::before {
  content: '•';
  color: #b87d2c;
}

.ingredient-group li span { min-width: 0; }
.ingredient-group li small {
  color: #a7443d;
  font-size: clamp(8px, .5vh, 10px);
  font-weight: 1000;
  text-transform: uppercase;
}
.ingredient-group li.unavailable span { opacity: .48; text-decoration: line-through; }

.crepe-products > .crepe-product:last-child .ingredient-groups { grid-template-columns: 1fr; }
.crepe-products > .crepe-product:last-child .ingredient-group ul { gap: 6px; }

@media (max-width: 900px) {
  .crepes-card { height: auto; padding: 16px; }
  :deep(.flag-label) { width: min(250px, 74%); font-size: 19px; }
  .crepe-products,
  .crepe-products.count-1 { grid-template-columns: 1fr; gap: 14px; margin-top: 14px; }
  .crepe-product { padding: 14px; }
  .crepe-product h3 { font-size: 23px; }
  .crepe-product header p { display: block; font-size: 13px; }
  .crepe-product header strong,
  .product-soldout { font-size: 13px; }
  .pricing-note { margin: 9px 0 12px; font-size: 13px; }
  .ingredient-groups,
  .crepe-products.count-1 .ingredient-groups { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
  .ingredient-group.wide { grid-column: 1 / -1; }
  .ingredient-group h4 { margin-bottom: 7px; font-size: 13px; }
  .ingredient-group li { font-size: 15px; line-height: 1.42; }
  .ingredient-group li small { font-size: 9px; }
}

@media (max-width: 520px) {
  .crepe-product > header { align-items: flex-start; }
  .crepe-product header strong,
  .product-soldout { padding: 6px 8px; }
  .ingredient-groups,
  .crepe-products.count-1 .ingredient-groups { grid-template-columns: 1fr; }
  .ingredient-group.wide { grid-column: auto; }
  .ingredient-group ul.columns { grid-template-columns: 1fr; }
}
</style>
